import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo
} from 'react'
import { useSearchParams } from 'react-router'

import {
  type SupplierDirectoryMode,
  type SupplierFilterState,
  filtersFromSearchParams,
  normalizeFilterSearchParams,
  withFilterSearchParams
} from './filter-state'

/* the url is the only copy of the filters, so Back and a shared link restore
 * them and a sidebar link to a bare path clears them. */
export function useUrlFilters(
  mode: SupplierDirectoryMode = 'all'
): [SupplierFilterState, Dispatch<SetStateAction<SupplierFilterState>>] {
  const [searchParams, setSearchParams] = useSearchParams()

  const filters = useMemo(
    () => filtersFromSearchParams(searchParams, mode),
    [searchParams, mode]
  )

  useEffect(() => {
    const canonical = normalizeFilterSearchParams(searchParams, mode)
    if (canonical.toString() !== searchParams.toString()) {
      setSearchParams(canonical, { replace: true })
    }
  }, [searchParams, setSearchParams, mode])

  const setFilters = useCallback(
    (action: SetStateAction<SupplierFilterState>) => {
      setSearchParams(
        prev => {
          const next =
            typeof action === 'function'
              ? action(filtersFromSearchParams(prev, mode))
              : action
          return withFilterSearchParams(prev, next, mode)
        },
        { replace: true }
      )
    },
    [setSearchParams, mode]
  )

  return [filters, setFilters]
}
